import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import RepricerLogsDao from '@/interfaces/repricer/RepricerLogs.dao'
import RepricerLogsMetadataDao from '@/interfaces/repricer/RepricerLogsMetadata.dao'
import RepricerOfferChangeTypesDao from '@/interfaces/repricer/RepricerOfferChangeTypes.dao'
import RepricerActionsDao from '@/interfaces/repricer/RepricerActions.dao'
import RepricerProcessStatusesDao from '@/interfaces/repricer/RepricerProcessStatuses.dao'
import RepricerUnitsDao from '@/interfaces/repricer/RepricerUnits.dao'
import RepricerExceptionsDao from '@/interfaces/repricer/RepricerExceptions.dao'
import RepricerAdminExceptionsDao from '@/interfaces/repricer/RepricerAdminExceptions.dao'
import RepricerUnitUserExceptionsDao from '@/interfaces/repricer/RepricerUnitUserExceptions.dao'
import RepricerUnitDao from '@/interfaces/repricer/RepricerUnit.dao'
import RepricerSummariesDao from '@/interfaces/repricer/RepricerSummaries.dao'
import RepricerStatusesDao from '@/interfaces/repricer/RepricerStatuses.dao'
import RepricerUnitDto from '@/interfaces/repricer/RepricerUnit.dto'
import SalesChannelsDao from '@/interfaces/repricer/SalesChannels.dao'
import { UnitAsinDao, UnitAsinNotificationDao } from '@/interfaces/repricer/UnitAsin.dao'
import BulkRepricerActionDto, { BulkRepricerActionResultDto } from '@/interfaces/repricer/BulkRepricerAction.dto'
import { RepricerListingDocDao, RepricerLogDetailDao } from '@/interfaces/repricer/RepricerLogPage.dao'
import { RepricerHealthDao } from '@/interfaces/repricer/RepricerHealth.dao'
import {
    RepricerListingStageCountsDao,
    RepricerListingStageRowsDao,
    RepricerListingStagesQuery,
} from '@/interfaces/repricer/RepricerListingStages.dao'

const LOG_PAGE_CALLER = 'repricerLogPage'

const HEALTH_PAGE_CALLER = 'repricerHealthPage'

const LISTINGS_COUNTS_CALLER = 'repricerListingStageCounts'

const LISTINGS_ROWS_CALLER = 'repricerListingStageRows'

const LOGS_LIST_CALLER = 'repricerLogsList'

const LOGS_EXPORT_CALLER = 'repricerLogsExport'

const QUIET = { suppressErrorAlert: true }

/**
 * The repricer API. The log lists, the log page and the review actions read the Amazon logs
 * (`RepricerLogs`); `RepricerCdiscountService` points the same methods at the cDiscount logs.
 */
export default class RepricerService extends ApiService {
    /** The logs endpoint of the channel. */
    protected readonly logsPath: string = '/repricer/repricer-logs'

    /** The endpoint of the repricer listing document behind a log of the channel. */
    protected readonly listingPath: string = '/repricer/repricer-listing'

    /** The Repricer Listings stages endpoint of the channel. */
    protected readonly listingStagesPath: string = '/repricer/listing-stages'

    /** The ending of the channel's review count and log action endpoints. */
    protected readonly actionSuffix: string = ''

    async getRepricerSummaries(
        params?: IRequestParameters,
    ): Promise<[RepricerSummariesDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerSummariesDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-summaries',
            params,
            undefined,
            undefined,
            'getRepricerSummaries',
        ).catch(() => null)
    }
    async getRepricerUnits(params?: IRequestParameters): Promise<[RepricerUnitsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerUnitsDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-units',
            params,
            undefined,
            undefined,
            'getRepricerUnits',
        ).catch(() => null)
    }
    async getRepricerUnit(id: string): Promise<RepricerUnitDao | null> {
        return this.request<undefined, RepricerUnitDao | null>(
            'GET',
            `/repricer/repricer-units/${encodeURIComponent(id)}`,
            undefined,
            undefined,
            undefined,
            'getRepricerUnit',
        ).catch(() => null)
    }

    /**
     * Toggles the unit's idle-nudge opt-in (`Configuration.IdleNudgeEnabled`,
     * consumed by the reNudgeIdleListings lambda). Returns the updated unit,
     * or null on failure.
     */
    async setRepricerUnitIdleNudge(unitId: number, enabled: boolean): Promise<RepricerUnitDao | null> {
        return this.request<{ enabled: boolean }, RepricerUnitDao | null>(
            'PATCH',
            `/repricer/repricer-units/${encodeURIComponent(String(unitId))}/idle-nudge`,
            undefined,
            { enabled },
            undefined,
            'setRepricerUnitIdleNudge',
        ).catch(() => null)
    }
    /**
     * Paginated raw (Group × ASIN) coverage for a unit — no enrichment, no
     * filters. The Affected ASINs page pages through this to load the unit's
     * full ASIN set, then expands it client-side against the unit's marketplaces.
     */
    async getUnitAsins(
        unitId: number,
        params?: IRequestParameters,
    ): Promise<[UnitAsinDao[], PageNumberPagination] | null> {
        return this.request<undefined, [UnitAsinDao[], PageNumberPagination] | null>(
            'GET',
            `/repricer/repricer-units/${encodeURIComponent(String(unitId))}/asins`,
            params,
            undefined,
            undefined,
            'getUnitAsins',
        ).catch(() => null)
    }

    /**
     * Batched sp-collector notification enrichment for a slice of a unit's
     * ASINs. Posts `{ asins, marketplaceIds }`; returns one row per
     * `(marketplace, ASIN)` pair that has an `spc-listings` document.
     */
    async getUnitAsinNotifications(
        unitId: number,
        body: { asins: string[]; marketplaceIds: string[] },
    ): Promise<UnitAsinNotificationDao[] | null> {
        return this.request<{ asins: string[]; marketplaceIds: string[] }, UnitAsinNotificationDao[]>(
            'POST',
            `/repricer/repricer-units/${encodeURIComponent(String(unitId))}/asins/notifications`,
            undefined,
            body,
            undefined,
            'getUnitAsinNotifications',
        ).catch(() => null)
    }
    async getRepricerExceptions(
        params?: IRequestParameters,
    ): Promise<[RepricerExceptionsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerExceptionsDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-exceptions',
            params,
            undefined,
            undefined,
            'getRepricerExceptions',
        ).catch(() => null)
    }
    async getRepricerAdminExceptions(
        params?: IRequestParameters,
    ): Promise<[RepricerAdminExceptionsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerAdminExceptionsDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-admin-exceptions',
            params,
            undefined,
            undefined,
            'getRepricerAdminExceptions',
        ).catch(() => null)
    }
    async getRepricerUnitUserExceptions(
        params?: IRequestParameters,
    ): Promise<[RepricerUnitUserExceptionsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerUnitUserExceptionsDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-unit-user-exceptions',
            params,
            undefined,
            undefined,
            'getRepricerUnitUserExceptions',
        ).catch(() => null)
    }
    async getRepricerListing(id: string): Promise<any[] | null> {
        return this.request<undefined, any[] | null>(
            'GET',
            `${this.listingPath}/${encodeURIComponent(id)}`,
            undefined,
            undefined,
            undefined,
            'getRepricerListing',
        ).catch(() => null)
    }
    async getRepricerOfferChangeTypes(
        params?: IRequestParameters,
    ): Promise<[RepricerOfferChangeTypesDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerOfferChangeTypesDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-offer-change-types',
            params,
            undefined,
            undefined,
            'getRepricerOfferChangeTypes',
        ).catch(() => null)
    }

    async getRepricerActions(
        params?: IRequestParameters,
    ): Promise<[RepricerActionsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerActionsDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-actions',
            params,
            undefined,
            undefined,
            'getRepricerActions',
        ).catch(() => null)
    }

    async getRepricerProcessStatuses(
        params?: IRequestParameters,
    ): Promise<[RepricerProcessStatusesDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerProcessStatusesDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-process-statuses',
            params,
            undefined,
            undefined,
            'getRepricerProcessStatuses',
        ).catch(() => null)
    }

    async getRepricerStatuses(
        params?: IRequestParameters,
    ): Promise<[RepricerStatusesDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerStatusesDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/repricer-statuses',
            params,
            undefined,
            undefined,
            'getRepricerStatuses',
        ).catch(() => null)
    }

    async getReviewUpdates(params?: IRequestParameters): Promise<number | null> {
        return this.request<undefined, number | null>(
            'GET',
            `/repricer/review-updates${this.actionSuffix}`,
            params,
            undefined,
            undefined,
            'getReviewUpdates',
        ).catch(() => null)
    }

    async getMetadata(params?: IRequestParameters): Promise<RepricerLogsMetadataDao | null> {
        return this.request<undefined, RepricerLogsMetadataDao | null>(
            'GET',
            '/repricer/metadata',
            params,
            undefined,
            undefined,
            'getMetadata',
        ).catch(() => null)
    }

    async getRepricerUnitsMap(params?: IRequestParameters): Promise<number[][] | null> {
        return this.request<undefined, number[][] | null>(
            'GET',
            '/repricer/repricer-units-map',
            params,
            undefined,
            undefined,
            'getRepricerUnitsMap',
        ).catch(() => null)
    }

    async releaseRepricerLog(id: number): Promise<RepricerLogsDao | null> {
        return this.request<undefined, RepricerLogsDao | null>(
            'PATCH',
            `/repricer/${id}/release-repricer-log${this.actionSuffix}`,
            undefined,
            undefined,
            undefined,
            'releaseRepricerLog',
        ).catch(() => null)
    }

    async actionApprove(id: number): Promise<RepricerLogsDao | null> {
        return this.request<undefined, RepricerLogsDao | null>(
            'PATCH',
            `/repricer/${id}/approve-repricer-log${this.actionSuffix}`,
            undefined,
            undefined,
            undefined,
            'actionApprove',
        ).catch(() => null)
    }

    async actionReject(id: number): Promise<RepricerLogsDao | null> {
        return this.request<undefined, RepricerLogsDao | null>(
            'PATCH',
            `/repricer/${id}/reject-repricer-log${this.actionSuffix}`,
            undefined,
            undefined,
            undefined,
            'actionReject',
        ).catch(() => null)
    }

    async actionApproveBulk(body: BulkRepricerActionDto): Promise<BulkRepricerActionResultDto | null> {
        return this.request<BulkRepricerActionDto, BulkRepricerActionResultDto | null>(
            'PATCH',
            `/repricer/bulk/approve-repricer-logs${this.actionSuffix}`,
            undefined,
            body,
            undefined,
            'actionApproveBulk',
        ).catch(() => null)
    }

    async actionRejectBulk(body: BulkRepricerActionDto): Promise<BulkRepricerActionResultDto | null> {
        return this.request<BulkRepricerActionDto, BulkRepricerActionResultDto | null>(
            'PATCH',
            `/repricer/bulk/reject-repricer-logs${this.actionSuffix}`,
            undefined,
            body,
            undefined,
            'actionRejectBulk',
        ).catch(() => null)
    }

    async requestRepricerUnitGenerateDescription(createRepricerUnitDto: RepricerUnitDto): Promise<any | null> {
        return this.request<RepricerUnitDto, any | null>(
            'POST',
            '/repricer/repricer-units/generate-description',
            undefined,
            createRepricerUnitDto,
            undefined,
            'requestRepricerUnitGenerateDescription',
        ).catch(() => null)
    }

    async createRepricerUnit(createRepricerUnitDto: RepricerUnitDto): Promise<any | null> {
        return this.request<RepricerUnitDto, any | null>(
            'POST',
            '/repricer/repricer-units',
            undefined,
            createRepricerUnitDto,
            undefined,
            'createRepricerUnit',
        ).catch(() => null)
    }

    async updateRepricerUnit(createRepricerUnitDto: RepricerUnitDto): Promise<any | null> {
        return this.request<RepricerUnitDto, any | null>(
            'PUT',
            '/repricer/repricer-units',
            undefined,
            createRepricerUnitDto,
            undefined,
            'updateRepricerUnit',
        ).catch(() => null)
    }

    async getSalesChannels(params?: IRequestParameters): Promise<[SalesChannelsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [SalesChannelsDao[], PageNumberPagination] | null>(
            'GET',
            '/repricer/sales-channels',
            params,
            undefined,
            undefined,
            'getSalesChannels',
        ).catch(() => null)
    }

    /** GET repricer-logs/:id for the log page; a failure throws, so the page can tell it from "not found". */
    async logPageLog(id: number): Promise<RepricerLogDetailDao | null> {
        return this.request<undefined, RepricerLogDetailDao | null>(
            'GET',
            `${this.logsPath}/${id}`,
            undefined,
            undefined,
            undefined,
            LOG_PAGE_CALLER,
            QUIET,
        )
    }

    /** GET repricer-listing/:id - the stored inputs of a log; a failure throws. */
    async logPageListing(id: string): Promise<RepricerListingDocDao[] | null> {
        return this.request<undefined, RepricerListingDocDao[] | null>(
            'GET',
            `${this.listingPath}/${encodeURIComponent(id)}`,
            undefined,
            undefined,
            undefined,
            LOG_PAGE_CALLER,
            QUIET,
        )
    }

    /** GET repricer-logs with a filter - the decisions around a log for the same listing; a failure throws. */
    async logPageAround(params: IRequestParameters): Promise<[RepricerLogDetailDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RepricerLogDetailDao[], PageNumberPagination] | null>(
            'GET',
            this.logsPath,
            params,
            undefined,
            undefined,
            LOG_PAGE_CALLER,
            QUIET,
        )
    }

    /** Cancels the log page reads in flight, when the page closes or opens another log. */
    cancelLogPage() {
        this.cancelRequest([LOG_PAGE_CALLER])
    }

    /** GET health - the latest hourly snapshot of the repricer flow; a failure throws. */
    async getHealth(): Promise<RepricerHealthDao> {
        return this.request<undefined, RepricerHealthDao>(
            'GET',
            '/repricer/health',
            undefined,
            undefined,
            undefined,
            HEALTH_PAGE_CALLER,
            QUIET,
        )
    }

    /** Cancels the Repricer health read in flight, when the page closes. */
    cancelHealth() {
        this.cancelRequest([HEALTH_PAGE_CALLER])
    }

    /** Cancels the Review Updates count of the repricer page head, when the page closes. */
    cancelReviewUpdates() {
        this.cancelRequest(['getReviewUpdates'])
    }

    /** GET listing-stages - documents per stage for the Repricer Listings filter; a failure throws. */
    async getListingStageCounts(query: RepricerListingStagesQuery): Promise<RepricerListingStageCountsDao> {
        return this.request<undefined, RepricerListingStageCountsDao>(
            'GET',
            this.listingStagesPath,
            { ...query },
            undefined,
            undefined,
            LISTINGS_COUNTS_CALLER,
            QUIET,
        )
    }

    /** GET listing-stages/rows - one page of a stage of the Repricer Listings page; a failure throws. */
    async getListingStageRows(query: RepricerListingStagesQuery): Promise<RepricerListingStageRowsDao> {
        return this.request<undefined, RepricerListingStageRowsDao>(
            'GET',
            `${this.listingStagesPath}/rows`,
            { ...query },
            undefined,
            undefined,
            LISTINGS_ROWS_CALLER,
            QUIET,
        )
    }

    /** Cancels the Repricer Listings reads in flight, before a new filter or when the page closes. */
    cancelListingStages() {
        this.cancelRequest([LISTINGS_COUNTS_CALLER, LISTINGS_ROWS_CALLER])
    }

    /** GET repricer-logs - one page of a logs list (Logs, Previous Updates, Review Updates); a failure throws. */
    async logsList(params: IRequestParameters): Promise<[RepricerLogDetailDao[], PageNumberPagination]> {
        return this.request<undefined, [RepricerLogDetailDao[], PageNumberPagination]>(
            'GET',
            this.logsPath,
            params,
            undefined,
            undefined,
            LOGS_LIST_CALLER,
            QUIET,
        )
    }

    /** GET repricer-logs/count - how many logs the same list parameters match; a failure throws. */
    async logsCount(params: IRequestParameters): Promise<number> {
        return this.request<undefined, number>(
            'GET',
            `${this.logsPath}/count`,
            params,
            undefined,
            undefined,
            LOGS_LIST_CALLER,
            QUIET,
        )
    }

    /** GET repricer-logs - one page of a CSV export; not cancelled by a filter change. A failure throws. */
    async logsExport(params: IRequestParameters): Promise<[RepricerLogDetailDao[], PageNumberPagination]> {
        return this.request<undefined, [RepricerLogDetailDao[], PageNumberPagination]>(
            'GET',
            this.logsPath,
            params,
            undefined,
            undefined,
            LOGS_EXPORT_CALLER,
            QUIET,
        )
    }

    /** Cancels the logs list reads in flight, before a new filter or when the page closes. */
    cancelLogsList() {
        this.cancelRequest([LOGS_LIST_CALLER])
    }

    /** Cancels a running CSV export of a logs list. */
    cancelLogsExport() {
        this.cancelRequest([LOGS_EXPORT_CALLER])
    }
}
